/**
 * Generic owned-resource reference for {@link PluginAuthzApi.canAccessOwnedResource}.
 *
 * A resource kind declares the permission slugs it is accessed under up
 * front; the access check then looks the right slug up by mode instead of
 * every surface hand-rolling its own owner/permission dispatch. Build the
 * slugs with {@link ownedResourcePermissions} so every kind follows the
 * same `<domain>:<mode>` convention — most kinds use the default
 * `read` / `write` pair, and a kind that needs more (say `create` or
 * `execute`) names its own modes. Kind-specific builders live with their
 * owning feature (e.g. `agentOwnedResource` in the server's agent auth
 * module), not in this framework file.
 *
 * Reaching a resource owned by *another* user is never a role: the check
 * resolves the mode's slug from a per-resource grant on that one resource
 * (`resource_grants`), so org-wide roles and the platform-admin flag
 * cannot widen it.
 */

/**
 * Permission slugs a resource kind is accessed under, looked up by access
 * mode. The kind decides its own mode vocabulary — most declare the
 * default `read` / `write` pair, but nothing here restricts a kind to
 * those two. Slugs must exist in the effective permission catalog — an
 * unknown slug fails closed at check time.
 */
export type OwnedResourcePermissions = Record<string, string>;

/**
 * An access mode valid for a resource kind's own permission map — the
 * lookup key for which slug an access check demands. Typing entry points
 * against the resource's map (rather than a fixed `'read' | 'write'`
 * union) is what lets a kind declare modes of its own.
 */
export type OwnedResourceMode<P extends OwnedResourcePermissions> = Extract<keyof P, string>;

/**
 * Build the access slugs for a permission domain, following the
 * framework-wide convention: `<domain>:<mode>` per declared mode. The
 * default modes are the common `read` (queries) / `write` (mutations)
 * pair; a kind that needs more names its own list. On a resource owned by
 * another user these are resolved from a per-resource grant only —
 * holding the same slug org-wide grants access to org-shared resources of
 * the kind, never to someone else's.
 */
export function ownedResourcePermissions(domain: string): Record<'read' | 'write', string>;
export function ownedResourcePermissions<const Mode extends string>(
  domain: string,
  modes: readonly Mode[],
): Record<Mode, string>;
export function ownedResourcePermissions(
  domain: string,
  modes: readonly string[] = ['read', 'write'],
): Record<string, string> {
  return Object.fromEntries(modes.map((mode) => [mode, `${domain}:${mode}`]));
}

/**
 * Derive the `:read` counterpart of a mutation-tier slug (`agents:write` →
 * `agents:read`, `projects:manage` → `projects:read`) — the raw string
 * derivation behind the write-implies-read contract, used by
 * `createOwnedResourceGate` and the test fakes.
 *
 * `write` and `manage` are the two standard mutation tiers, so this covers
 * every ordinary write gate. It still returns `undefined` for a mutating
 * slug with a verb of its own (`plugins:install`); those name their read
 * slug explicitly. The host has the richer, catalog-driven answer
 * (`impliedReadPermissionSlug`), which plugin-api cannot reach — it holds
 * no catalog, so the derived slug is not validated here. An uncataloged
 * slug fails closed at check time, and the posture that names it is
 * rejected at plugin registration by the host's catalog validation.
 */
export function deriveImpliedReadSlug(permission: string): string | undefined {
  const colon = permission.indexOf(':');
  if (colon === -1) return undefined;
  const action = permission.slice(colon + 1);
  return action === 'write' || action === 'manage'
    ? `${permission.slice(0, colon)}:read`
    : undefined;
}

export interface OwnedResource {
  /**
   * Resource type as recorded in per-resource ACL grants
   * (`resource_grants.resource_type`, `ResourceReference.type`), e.g.
   * `'agent'`. A grant conferring the mode's slug on `{type, id}` is what
   * satisfies the check for that one resource.
   */
  type: string;
  id: string;
  /**
   * Owning user id. Every resource must have a known owner — pass the
   * creating user's id. A caller who owns the resource always has access;
   * every other caller needs a per-resource grant on it.
   */
  ownerId: string;
  /** Access slugs for this resource kind, looked up by access mode. */
  permissions: OwnedResourcePermissions;
}

/**
 * Outcome of an owner/ACL access check: allowed, or denied together with the
 * slug a per-resource grant would have had to confer (for a `403` body / log).
 */
export type OwnedResourceAccess = { allowed: true } | { allowed: false; permission: string };

/**
 * The identity an authorization decision resolves against.
 *
 * Every authz entry point takes this one shape. A Fastify request is just a
 * carrier for it — convert with {@link callerOf} at the call site rather
 * than giving each check a second request-shaped twin, which is how the
 * request/identity pairs multiply.
 */
export interface AuthzCaller {
  userId?: string;
  organizationId?: string | null;
  isSuperAdmin?: boolean;
}

/**
 * The caller identity the host's auth middleware stamped on a request.
 *
 * Typed against `unknown` because plugin-api describes the request
 * structurally; an unauthenticated or org-less request yields empty fields,
 * which every check treats as fail-closed.
 *
 * `isSuperAdmin` is deliberately not carried over. No check in this API
 * bypasses on it, so forwarding the request's flag would only suggest one
 * does.
 */
export function callerOf(request: unknown): AuthzCaller {
  const stamped = request as
    | { currentUser?: { id?: string } | null; currentOrganizationId?: string | null }
    | null
    | undefined;
  return {
    ...(stamped?.currentUser?.id ? { userId: stamped.currentUser.id } : {}),
    organizationId: stamped?.currentOrganizationId ?? null,
  };
}

/**
 * Minimal authz surface {@link checkOwnedResourceAccess} depends on — just the
 * one method it calls. Kept structural (rather than importing the full
 * `PluginAuthzApi`) so this leaf module stays free of a framework import cycle;
 * the real `PluginAuthzApi` satisfies it.
 */
export interface OwnedResourceAuthz {
  canAccessOwnedResource(
    caller: AuthzCaller,
    resource: OwnedResource,
    mode: string,
  ): Promise<OwnedResourceAccess>;
}

/**
 * The denial shape for an owned-resource decision: not allowed, naming the
 * slug a grant would need to confer for `mode`. The one way to build a
 * denial, shared by {@link checkOwnedResourceAccess} and every caller that
 * builds one directly, so the shape cannot drift.
 */
export function ownedResourceAccessDenied<P extends OwnedResourcePermissions>(
  resource: { permissions: P },
  mode: OwnedResourceMode<P>,
): OwnedResourceAccess {
  return { allowed: false, permission: resource.permissions[mode] };
}

/**
 * Owner/ACL access gate on top of a resource's base permission, with the
 * framework's fail-closed posture baked in so every plugin surface enforces
 * it the same way instead of hand-rolling the dispatch:
 *
 *  - **No recorded owner** (null/empty `ownerId`) → deny. Every owned
 *    resource is expected to carry a real owner — a user, or a service
 *    account (see {@link OwnedResource.ownerId}). A missing owner is an
 *    anomaly, not an org-shared fallback: we fail closed rather than grant
 *    access on the base permission alone.
 *  - **A real owner** → delegate to `canAccessOwnedResource`: self-owned and
 *    service-account-owned pass; any other owner needs a per-resource grant
 *    conferring the mode's slug on this specific resource.
 *
 * The base permission slug is assumed already enforced by the route's
 * preHandler; this adds only the owner scoping on top.
 *
 * `authz` is required, not optional: authorization is not a service a
 * surface can be wired without. The host always provides it, and a plugin
 * that needs it declares it in the services it registers with.
 */
export async function checkOwnedResourceAccess<P extends OwnedResourcePermissions>(
  authz: OwnedResourceAuthz,
  caller: AuthzCaller,
  resource: {
    type: string;
    id: string;
    ownerId: string | null;
    permissions: P;
  },
  mode: OwnedResourceMode<P>,
): Promise<OwnedResourceAccess> {
  // No recorded owner → fail closed. We do not treat an owner-less resource
  // as org-shared; the base permission alone must not grant access.
  if (!resource.ownerId) {
    return ownedResourceAccessDenied(resource, mode);
  }
  return authz.canAccessOwnedResource(
    caller,
    {
      type: resource.type,
      id: resource.id,
      ownerId: resource.ownerId,
      permissions: resource.permissions,
    },
    mode,
  );
}
